/**
 * Utility functions for IP address detection and management
 */

/**
 * Fetch the user's current IP address using a public IP detection service
 * @returns Promise<string | null> - The user's IP address or null if detection fails
 */
export const fetchUserIp = async (): Promise<string | null> => {
  try {
    // Try multiple IP detection services as fallback
    const ipServices = [
      { url: 'https://api.ipify.org?format=json', key: 'ip' },
      { url: 'https://api.my-ip.io/ip', key: null }, // Returns plain text
      { url: 'https://ipapi.co/json/', key: 'ip' },
    ];

    for (const service of ipServices) {
      try {
        // Use AbortController for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await fetch(service.url, {
          method: 'GET',
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          const data = await response.json();
          const ip = service.key ? data[service.key] : data;

          if (ip && typeof ip === 'string' && isValidIp(ip)) {
            console.log('[IP Detection] Successfully fetched IP:', ip);
            return ip;
          }
        }
      } catch (e) {
        console.warn(`[IP Detection] Service ${service.url} failed:`, e);
        // Continue to next service
      }
    }

    // If plain text service works
    try {
      const response = await fetch('https://api.my-ip.io/ip');
      if (response.ok) {
        const ip = await response.text();
        if (ip && isValidIp(ip.trim())) {
          console.log('[IP Detection] Successfully fetched IP from text service:', ip);
          return ip.trim();
        }
      }
    } catch (e) {
      console.warn('[IP Detection] Plain text service failed:', e);
    }

    console.warn('[IP Detection] All IP detection services failed');
    return null;
  } catch (error) {
    console.error('[IP Detection] Unexpected error:', error);
    return null;
  }
};

/**
 * Validate if a string is a valid IP address (IPv4 or IPv6)
 */
export const isValidIp = (ip: string): boolean => {
  // Simple IPv4 validation
  const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
  // Simple IPv6 validation
  const ipv6Regex = /^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4})$/;

  return ipv4Regex.test(ip) || ipv6Regex.test(ip);
};

/**
 * Store user IP in localStorage
 */
export const storeUserIp = (ip: string): void => {
  try {
    localStorage.setItem('userClientIp', ip);
    localStorage.setItem('userClientIpTimestamp', new Date().toISOString());
  } catch (e) {
    console.warn('[IP Storage] Failed to store IP:', e);
  }
};

/**
 * Retrieve stored user IP from localStorage
 */
export const getStoredUserIp = (): string | null => {
  try {
    return localStorage.getItem('userClientIp');
  } catch (e) {
    console.warn('[IP Storage] Failed to retrieve IP:', e);
    return null;
  }
};

/**
 * Clear stored user IP from localStorage
 */
export const clearStoredUserIp = (): void => {
  try {
    localStorage.removeItem('userClientIp');
    localStorage.removeItem('userClientIpTimestamp');
  } catch (e) {
    console.warn('[IP Storage] Failed to clear IP:', e);
  }
};

/**
 * Get or fetch user IP (tries localStorage first, then fetches if needed)
 */
export const getUserIp = async (): Promise<string | null> => {
  const stored = getStoredUserIp();
  if (stored) {
    return stored;
  }

  const ip = await fetchUserIp();
  if (ip) {
    storeUserIp(ip);
  }

  return ip;
};
